// Vertical timeline of alerts — merged stream of live webhook captures (sub-second
// via SignalR) and persisted alerts from the orchestrator DB. Each row is collapsible
// and shows the rich JSON body + signature headers on expand.

import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ArrowUpRight,
  ArrowDownRight,
  ChevronRight,
  Database,
  Radio,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { AlertSummaryDto, WebhookCaptureDto } from "../../api/types";
import { parseBackendTimestamp } from "../../hooks/time";

interface Props {
  alerts: AlertSummaryDto[];
  captures: WebhookCaptureDto[];
}

type TimelineEntry =
  | { kind: "capture"; at: number; capture: WebhookCaptureDto }
  | { kind: "alert"; at: number; alert: AlertSummaryDto };

export function AlertTimeline({ alerts, captures }: Props) {
  const merged: TimelineEntry[] = [
    ...captures.map<TimelineEntry>((c) => ({
      kind: "capture",
      at: parseBackendTimestamp(c.receivedAt),
      capture: c,
    })),
    ...alerts.map<TimelineEntry>((a) => ({
      kind: "alert",
      at: parseBackendTimestamp(a.signalBarTime),
      alert: a,
    })),
  ]
    .filter((e) => Number.isFinite(e.at))
    .sort((a, b) => b.at - a.at);

  if (merged.length === 0) {
    return (
      <Card className="p-6 text-center bg-card-elevated">
        <Radio className="size-6 mx-auto mb-2 text-muted-foreground" />
        <h3 className="text-sm font-semibold mb-1">No alerts yet</h3>
        <p className="text-xs text-muted-foreground max-w-md mx-auto">
          Activate this strategy and wait for the next candle close — fires will appear
          here in real time. You can also "Test-fire" from the Strategies tab to inject
          a synthetic alert.
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-4 bg-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold tracking-tight">Alert timeline</h3>
        <div className="text-[11px] text-muted-foreground font-mono">
          {captures.length} live · {alerts.length} persisted
        </div>
      </div>
      <div className="relative pl-6">
        {/* Spine */}
        <div className="absolute left-2 top-1 bottom-1 w-px bg-border" />
        <div className="space-y-2">
          {merged.map((entry, i) => (
            // Stable identity (not array index): each TimelineRow holds its own expand state, and
            // `merged` re-sorts as new entries arrive — index keys would make the open state stick
            // to a position rather than to the entry.
            <TimelineRow
              key={
                entry.kind === "alert"
                  ? `a-${entry.alert.id}`
                  : `c-${entry.capture.receivedAt}-${entry.capture.headers?.["X-Webhook-Fingerprint"] ?? i}`
              }
              entry={entry}
            />
          ))}
        </div>
      </div>
    </Card>
  );
}

function TimelineRow({ entry }: { entry: TimelineEntry }) {
  const [open, setOpen] = useState(false);
  const direction =
    entry.kind === "capture"
      ? directionFromBody(entry.capture.body)
      : entry.alert.direction === "long" || entry.alert.direction === "short"
        ? entry.alert.direction
        : null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="relative">
        {/* Spine dot */}
        <div
          className={cn(
            "absolute -left-[18px] top-2 size-3 rounded-full ring-2 ring-card",
            direction === "long" && "bg-emerald-500",
            direction === "short" && "bg-red-500",
            !direction && "bg-muted-foreground",
          )}
        />

        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full text-left flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-card-elevated transition-colors"
          >
            <div className="flex items-center gap-2 min-w-0">
              {entry.kind === "capture"
                ? <Radio className="size-3.5 text-primary" />
                : <Database className="size-3.5 text-muted-foreground" />}
              {direction === "long" && <ArrowUpRight className="size-3.5 text-emerald-500" />}
              {direction === "short" && <ArrowDownRight className="size-3.5 text-red-500" />}
              {direction && (
                <Badge variant="outline" className={cn(
                  "font-mono text-[10px]",
                  direction === "long" && "border-emerald-500/40 text-emerald-400",
                  direction === "short" && "border-red-500/40 text-red-400",
                )}>
                  {direction.toUpperCase()}
                </Badge>
              )}
              <span className="text-xs font-mono text-muted-foreground">
                {new Date(entry.at).toLocaleString()}
              </span>
              {entry.kind === "alert" && (
                <Badge variant="secondary" className="text-[10px]">
                  {entry.alert.deliveryStatus}
                </Badge>
              )}
            </div>
            <ChevronRight className={cn("size-3.5 text-muted-foreground transition-transform shrink-0", open && "rotate-90")} />
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <pre className="mt-2 ml-2 p-3 rounded-md bg-card-elevated border text-[11px] font-mono overflow-x-auto max-w-full">
            {entry.kind === "capture" ? formatCapture(entry.capture) : formatAlert(entry.alert)}
          </pre>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function directionFromBody(body: unknown): "long" | "short" | null {
  if (body && typeof body === "object" && "direction" in body) {
    const d = (body as { direction?: unknown }).direction;
    if (d === "long" || d === "short") return d;
  }
  return null;
}

function formatCapture(cap: WebhookCaptureDto): string {
  const headers = Object.entries(cap.headers)
    .map(([k, v]) => `  ${k}: ${v}`)
    .join("\n");
  const body = cap.body == null ? "(empty)" : JSON.stringify(cap.body, null, 2);
  return `# Headers\n${headers}\n\n# Body\n${body}`;
}

function formatAlert(alert: AlertSummaryDto): string {
  return JSON.stringify(alert, null, 2);
}
